import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Search, Loader2, X, ShieldAlert, PlayCircle, Star, Dumbbell } from 'lucide-react';
import { Card, Chip, Empty } from './shared';
import { api } from '../../lib/api';
import { youtubeVideoUrl, hasCuratedVideo } from '../../lib/exerciseVideos';

const ExerciseCard = ({ ex, onOpen, isFav, onToggleFav }) => (
  <div className="relative">
    <button
      onClick={() => onOpen(ex)}
      className="w-full text-left rounded-xl border border-[#1f2937] bg-[#0b0c10] hover:border-[#ff7a1a]/60 transition-colors p-4 flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2 pr-8">
        <h4 className="text-white font-bold text-[15px] leading-tight">{ex.name}</h4>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${DIFFICULTY_COLOR[ex.difficulty] || ''}`}>
          {ex.difficulty}
        </span>
      </div>
      <p className="text-[#7c8597] text-[11px] uppercase tracking-wider font-bold">{ex.category}</p>
      <div className="flex flex-wrap gap-1 mt-1">
        {(ex.muscles || []).slice(0, 3).map((m) => (
          <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1a0f06] text-[#ff7a1a] font-semibold">{m}</span>
        ))}
      </div>
      <p className="text-[#aab1bf] text-[11px] mt-1">{(ex.equipment || []).join(' · ')}</p>
    </button>

    {/* Always-visible Watch demo + Log set — one tap each */}
    <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('open-log-sheet', { detail: { exercise: ex } }));
        }}
        data-testid={`card-log-set-${ex.id}`}
        aria-label={`Log a set for ${ex.name}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#BEFF38] text-black text-[10px] font-black uppercase tracking-wider hover:bg-lime-300 active:scale-95 transition-transform"
      >
        <Dumbbell className="w-3 h-3" /> Log
      </button>
      <a
        href={youtubeVideoUrl(ex.name)}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        data-testid={`card-watch-demo-${ex.id}`}
        aria-label={`Watch demo for ${ex.name}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#ff7a1a] text-[#0b0b0c] text-[10px] font-black uppercase tracking-wider hover:bg-[#ff8a33] active:scale-95 transition-transform"
      >
        <PlayCircle className="w-3 h-3" /> Demo
        {hasCuratedVideo(ex.name) && (
          <span
            data-testid={`card-curated-badge-${ex.id}`}
            className="ml-0.5 px-1 rounded-full bg-[#BEFF38] text-black text-[8px] font-black"
          >★</span>
        )}
      </a>
    </div>

    <button
      onClick={(e) => { e.stopPropagation(); onToggleFav(ex); }}
      data-testid={`fav-btn-${ex.id}`}
      className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full hover:bg-zinc-800/60"
      aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
    >
      <Star
        className={`h-4 w-4 ${isFav ? "fill-amber-400 text-amber-400" : "text-zinc-500"}`}
        strokeWidth={2}
      />
    </button>
  </div>
);

const DIFFICULTY_COLOR = {
  Beginner: 'text-emerald-400 border-emerald-500/40',
  Intermediate: 'text-amber-400 border-amber-500/40',
  Advanced: 'text-rose-400 border-rose-500/40',
};

const ExerciseModal = ({ ex, onClose }) => {
  if (!ex) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="max-w-lg w-full bg-[#0f1115] border border-[#2a2f3a] rounded-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 lg:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-white font-extrabold text-2xl leading-tight">{ex.name}</h3>
              <p className="text-[#7c8597] text-xs uppercase tracking-wider font-bold mt-1">
                {ex.category} · {ex.difficulty}
              </p>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#0b0c10] border border-[#2a2f3a] text-white flex items-center justify-center hover:border-[#ff7a1a]">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-1.5">
            {(ex.muscles || []).map((m) => (
              <span key={m} className="text-xs px-2 py-0.5 rounded bg-[#1a0f06] text-[#ff7a1a] font-semibold">{m}</span>
            ))}
          </div>
          <p className="text-[#aab1bf] text-sm mt-3">Equipment: {(ex.equipment || []).join(', ')}</p>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <a
              href={youtubeVideoUrl(ex.name)}
              target="_blank"
              rel="noreferrer"
              data-testid="exercise-watch-demo-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ff7a1a] text-[#0b0b0c] font-bold text-sm hover:bg-[#ff8a33] transition-colors"
            >
              <PlayCircle className="w-4 h-4" /> Watch demo
            </a>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-log-sheet', { detail: { exercise: ex } }));
                onClose();
              }}
              data-testid="exercise-modal-log-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#BEFF38] text-black font-bold text-sm hover:bg-lime-300 transition-colors"
            >
              <Dumbbell className="w-4 h-4" /> Log a set
            </button>
            {hasCuratedVideo(ex.name) && (
              <span
                data-testid="exercise-curated-badge"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#BEFF38]/40 bg-[#BEFF38]/10 text-[#BEFF38] text-[10px] font-bold uppercase tracking-wider"
              >
                <Star className="w-3 h-3 fill-[#BEFF38]" /> Curated
              </span>
            )}
          </div>

          {ex.cues && ex.cues.length > 0 && (
            <div className="mt-5">
              <p className="text-[#ff7a1a] text-xs uppercase tracking-wider font-bold">Form cues</p>
              <ul className="mt-2 space-y-1.5">
                {ex.cues.map((c, i) => (
                  <li key={i} className="text-[#d3d8e0] text-sm leading-snug flex gap-2">
                    <span className="text-[#ff7a1a]">•</span><span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(ex.contraindications) && ex.contraindications.length > 0 && (
            <div className="mt-5 rounded-lg border border-[#3a2310] bg-[#1a0f06]/60 p-3">
              <p className="text-[#ff7a1a] font-bold text-xs uppercase tracking-wide flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> Caution if you have
              </p>
              <p className="text-[#cbb39a] text-xs mt-1">{ex.contraindications.join(' · ')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExerciseLibrary = () => {
  const [facets, setFacets] = useState({ categories: [], equipment: [], muscles: [], difficulties: [], total: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', equipment: '', muscle: '', difficulty: '', search: '' });
  const [active, setActive] = useState(null);
  const [favIds, setFavIds] = useState(new Set());
  const [favOnly, setFavOnly] = useState(false);

  useEffect(() => {
    api.exerciseFacets().then(setFacets).catch(() => {});
    api.favouriteIds().then((ids) => setFavIds(new Set(ids || []))).catch(() => {});
  }, []);

  const toggleFav = async (ex) => {
    const next = new Set(favIds);
    if (next.has(ex.id)) {
      next.delete(ex.id);
      setFavIds(next);
      try { await api.removeFavourite(ex.id); } catch {}
    } else {
      next.add(ex.id);
      setFavIds(next);
      try { await api.addFavourite(ex.id, ex.name); } catch {}
    }
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.exercises(params)
      .then((d) => setItems(d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const clear = () => setFilters({ category: '', equipment: '', muscle: '', difficulty: '', search: '' });
  const hasFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);
  const set = (k) => (v) => setFilters({ ...filters, [k]: filters[k] === v ? '' : v });

  return (
    <Card title={`Exercise library (${facets.total || 0})`} icon={BookOpen}>
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[#7c8597] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search exercises…"
            className="w-full pl-9 pr-3 py-2.5 bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg text-white outline-none text-sm"
          />
        </div>

        <div className="space-y-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597] mb-1.5">Category</p>
            <div className="flex flex-wrap gap-1.5">
              {facets.categories.map((c) => (
                <Chip key={c} active={filters.category === c} onClick={() => set('category')(c)}>{c}</Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597] mb-1.5">Equipment</p>
            <div className="flex flex-wrap gap-1.5">
              {facets.equipment.map((c) => (
                <Chip key={c} active={filters.equipment === c} onClick={() => set('equipment')(c)}>{c}</Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597] mb-1.5">Difficulty</p>
            <div className="flex flex-wrap gap-1.5">
              {facets.difficulties.map((c) => (
                <Chip key={c} active={filters.difficulty === c} onClick={() => set('difficulty')(c)}>{c}</Chip>
              ))}
            </div>
          </div>
          {hasFilters && (
            <button onClick={clear} className="text-xs text-[#ff7a1a] font-semibold hover:underline">
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-8 flex items-center gap-2 text-[#aab1bf] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading exercises…
          </div>
        ) : items.length === 0 ? (
          <Empty>No exercises match those filters. Try clearing one.</Empty>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-[#7c8597]">
                {(favOnly ? items.filter((e) => favIds.has(e.id)) : items).length} exercise
                {(favOnly ? items.filter((e) => favIds.has(e.id)) : items).length === 1 ? '' : 's'}
              </p>
              <button
                onClick={() => setFavOnly(!favOnly)}
                data-testid="favourites-only-toggle"
                className={`text-xs font-semibold flex items-center gap-1 px-2.5 py-1 rounded-full border transition-colors ${
                  favOnly
                    ? "bg-amber-400/15 border-amber-400 text-amber-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                <Star className={`h-3.5 w-3.5 ${favOnly ? "fill-amber-400 text-amber-400" : ""}`} />
                {favOnly ? `Favourites (${favIds.size})` : `Show favourites${favIds.size ? ` (${favIds.size})` : ''}`}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(favOnly ? items.filter((e) => favIds.has(e.id)) : items).map((ex) => (
                <ExerciseCard
                  key={ex.id}
                  ex={ex}
                  onOpen={setActive}
                  isFav={favIds.has(ex.id)}
                  onToggleFav={toggleFav}
                />
              ))}
            </div>
            {favOnly && favIds.size === 0 && (
              <Empty>You haven't starred any exercises yet. Tap the star on any card to add it.</Empty>
            )}
          </>
        )}
      </div>

      <ExerciseModal ex={active} onClose={() => setActive(null)} />
    </Card>
  );
};

export default ExerciseLibrary;
